import { Link } from "react-router-dom";
import { Radio } from "lucide-react";

import type { CallListItem } from "../../api/calls";

/**
 * Live-call hero card. Glowing top hairline + pulsing radio glyph. Big
 * elapsed clock. Operator's "scope" — the thing they stare at while a
 * call is in flight.
 */
export function LiveCard({ call }: { call: CallListItem }) {
  return (
    <Link
      to={`/live-calls/${call.call_id}`}
      style={{
        position: "relative",
        display: "block",
        textDecoration: "none",
        background: "linear-gradient(180deg, var(--bg-card-hero), var(--bg-card))",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-card)",
        padding: "16px 18px",
        color: "var(--text-secondary)",
        overflow: "hidden",
      }}
    >
      {/* top accent glow line */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background: "linear-gradient(90deg, transparent, var(--accent), transparent)",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--accent)",
          }}
        >
          <Radio size={11} strokeWidth={1.75} className="live-pulse" />
          LIVE
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--text-muted)",
            letterSpacing: "0.08em",
          }}
        >
          {call.call_id.slice(0, 8)}
        </span>
      </div>

      <div
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 32,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: "var(--text-primary)",
          lineHeight: 1,
        }}
      >
        {elapsedSince(call.started_at)}
      </div>

      <div
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: "1px solid var(--border-subtle)",
          display: "flex",
          justifyContent: "space-between",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          letterSpacing: "0.04em",
        }}
      >
        <Metric label="TURN" value={String(call.turn_count)} />
        <Metric
          label="REJECT"
          value={String(call.rejection_count)}
          tone={call.rejection_count > 0 ? "warn" : "muted"}
        />
        <Metric
          label="REC"
          value={call.has_recording ? "ON" : "—"}
          tone={call.has_recording ? "ok" : "muted"}
        />
      </div>
    </Link>
  );
}

function Metric({
  label,
  value,
  tone = "muted",
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "muted";
}) {
  const color =
    tone === "ok"
      ? "var(--success)"
      : tone === "warn"
        ? "var(--warning)"
        : "var(--text-secondary)";
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
      <span style={{ color: "var(--text-muted)", fontSize: 9.5, letterSpacing: "0.16em" }}>
        {label}
      </span>
      <span style={{ color, fontSize: 13, fontWeight: 500 }}>{value}</span>
    </span>
  );
}

function elapsedSince(iso: string): string {
  const start = new Date(iso).getTime();
  const secs = Math.floor((Date.now() - start) / 1000);
  const m = Math.floor(secs / 60);
  const s = secs - m * 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
